import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Package, Factory, FileCheck, BarChart3, Users,
  Search, LogOut, Menu, X, Loader2, ArrowLeft,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../api";
import BrandMark from "./BrandMark";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/inventory", label: "Inventory", icon: Package },
  { to: "/manufacturing", label: "Manufacturing", icon: Factory },
  { to: "/packing-list", label: "Packing List", icon: FileCheck },
  { to: "/reports", label: "Reports", icon: BarChart3 },
];

function GlobalSearch() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (q.trim().length < 2) { setResults(null); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/search", { params: { q } });
        setResults(r.data);
        setOpen(true);
      } catch (e) { /* ignore */ }
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const go = (path) => { setOpen(false); setQ(""); navigate(path); };
  const empty = results && Object.values(results).every((a) => a.length === 0);

  return (
    <div ref={boxRef} className="relative w-full max-w-md">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
      <input
        data-testid="global-search-input"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => results && setOpen(true)}
        placeholder="Search lot, MO, product, customer, invoice..."
        className="w-full h-10 pl-9 pr-3 rounded-md border border-border bg-slate-50 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
      />
      {open && results && (
        <div data-testid="global-search-results" className="absolute mt-1 w-full bg-white border border-border rounded-md shadow-lg z-50 max-h-96 overflow-auto text-sm">
          {empty && <div className="px-4 py-3 text-slate-500">No results</div>}
          {results.lots.length > 0 && (
            <div>
              <div className="px-4 pt-2 pb-1 text-[10px] font-mono uppercase tracking-wider text-slate-400">Lots</div>
              {results.lots.map((l) => (
                <button key={l.id} data-testid={`search-lot-${l.lot_number}`} onClick={() => go(`/lots/${l.id}`)} className="w-full text-left px-4 py-2 hover:bg-slate-50">
                  <span className="font-mono font-semibold text-[#002B5D]">{l.lot_number}</span>
                  <span className="text-slate-500 ml-2">{l.product_name}</span>
                </button>
              ))}
            </div>
          )}
          {results.mos.length > 0 && (
            <div>
              <div className="px-4 pt-2 pb-1 text-[10px] font-mono uppercase tracking-wider text-slate-400">Manufacturing Orders</div>
              {results.mos.map((m) => (
                <button key={m.id} data-testid={`search-mo-${m.mo_number}`} onClick={() => go(`/manufacturing/orders/${m.id}`)} className="w-full text-left px-4 py-2 hover:bg-slate-50">
                  <span className="font-mono font-semibold text-[#002B5D]">{m.mo_number}</span>
                  <span className="text-slate-500 ml-2">{m.product_name}</span>
                </button>
              ))}
            </div>
          )}
          {results.products.length > 0 && (
            <div>
              <div className="px-4 pt-2 pb-1 text-[10px] font-mono uppercase tracking-wider text-slate-400">Products</div>
              {results.products.map((p) => (
                <button key={p.id} data-testid={`search-product-${p.code}`} onClick={() => go(`/inventory?search=${encodeURIComponent(p.code)}`)} className="w-full text-left px-4 py-2 hover:bg-slate-50">
                  <span className="font-mono font-semibold text-[#002B5D]">{p.code}</span>
                  <span className="text-slate-500 ml-2">{p.name}</span>
                </button>
              ))}
            </div>
          )}
          {results.packing_lists.length > 0 && (
            <div>
              <div className="px-4 pt-2 pb-1 text-[10px] font-mono uppercase tracking-wider text-slate-400">Packing Lists</div>
              {results.packing_lists.map((p) => (
                <button key={p.id} data-testid={`search-pl-${p.pl_number}`} onClick={() => go(`/packing-list/${p.id}`)} className="w-full text-left px-4 py-2 hover:bg-slate-50">
                  <span className="font-mono font-semibold text-[#002B5D]">{p.pl_number}</span>
                  <span className="text-slate-500 ml-2">{p.customer}</span>
                </button>
              ))}
            </div>
          )}
          {results.deductions.length > 0 && (
            <div>
              <div className="px-4 pt-2 pb-1 text-[10px] font-mono uppercase tracking-wider text-slate-400">Deductions</div>
              {results.deductions.map((d) => (
                <button key={d.id} data-testid={`search-deduction-${d.id}`} onClick={() => go(`/lots/${d.lot_id}`)} className="w-full text-left px-4 py-2 hover:bg-slate-50">
                  <span className="font-mono font-semibold text-[#002B5D]">{d.lot_number}</span>
                  <span className="text-slate-500 ml-2">{d.customer_name} {d.reference_no}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const doLogout = async () => { await logout(); navigate("/login"); };

  const navItems = [...NAV];
  if (user?.role === "admin") navItems.push({ to: "/settings/users", label: "Users", icon: Users });

  const nav = (
    <nav className="flex-1 px-3 py-4 space-y-1" data-testid="sidebar-nav">
      {navItems.map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          end={n.end}
          onClick={() => setMobileOpen(false)}
          data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
              isActive ? "bg-[#EB1C27] text-white" : "text-blue-100 hover:bg-[#0E3B75] hover:text-white"
            }`
          }
        >
          <n.icon className="w-5 h-5 shrink-0" />
          {n.label}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen flex" data-testid="app-layout">
      <aside className="hidden md:flex w-60 flex-col fixed inset-y-0 z-30" style={{ backgroundColor: "#002B5D" }}>
        <div className="px-4 py-5 border-b border-white/10">
          <BrandMark size={38} />
        </div>
        {nav}
        <div className="px-4 py-4 border-t border-white/10">
          <div className="text-white text-sm font-semibold truncate" data-testid="sidebar-user-name">{user?.name}</div>
          <div className="text-blue-300 text-xs capitalize" data-testid="sidebar-user-role">{user?.role}</div>
          <button data-testid="logout-button" onClick={doLogout} className="mt-3 flex items-center gap-2 text-blue-200 hover:text-white text-sm transition-colors">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden" data-testid="mobile-nav-overlay">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-64 flex flex-col" style={{ backgroundColor: "#002B5D" }}>
            <div className="px-4 py-5 border-b border-white/10 flex items-center justify-between">
              <BrandMark size={34} />
              <button onClick={() => setMobileOpen(false)} className="text-white"><X className="w-6 h-6" /></button>
            </div>
            {nav}
            <div className="px-4 py-4 border-t border-white/10">
              <div className="text-white text-sm font-semibold">{user?.name}</div>
              <button data-testid="mobile-logout-button" onClick={doLogout} className="mt-2 flex items-center gap-2 text-blue-200 text-sm">
                <LogOut className="w-4 h-4" /> Sign out
              </button>
            </div>
          </aside>
        </div>
      )}

      <div className="flex-1 md:ml-60 flex flex-col min-w-0">
        <header className="sticky top-0 z-20 bg-white border-b border-border px-4 py-3 flex items-center gap-3">
          <button data-testid="mobile-menu-button" className="md:hidden p-1" onClick={() => setMobileOpen(true)}>
            <Menu className="w-6 h-6 text-[#002B5D]" />
          </button>
          <div className="md:hidden"><BrandMark size={30} dark /></div>
          <button data-testid="back-button" onClick={() => navigate(-1)} title="Go back"
            className="hidden md:inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-border text-sm font-semibold text-[#002B5D] hover:bg-slate-50 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="flex-1 flex justify-end md:justify-center">
            <GlobalSearch />
          </div>
        </header>
        <main className="flex-1 p-4 md:p-8 max-w-[1400px] w-full mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
